import queryString from 'query-string';
import retryAsyncPromise, { RetryOptions } from "../util/retryAsync"

interface AuthData {
  type: string
  token?: string | null
}

interface CachedResponse {
  expires: number
  response: any
}

export type Verb = 'get' | 'post' | 'put' | 'delete';

export type VerifyResponseFn = (
  fetchResponse: Response,
  response: Response | Blob | any,
  url: string,
  options: FetchOptions,
) => Promise<void>

export type FetchApiHelperFn = (url: string, options: FetchOptions, api: FetchApi | FetchApiHelperFn) => any

export interface FetchOptions {
  xsrf?: boolean
  signal?: AbortSignal
  abortController?: AbortController
  auth?: AuthData
  authHeader?: string
  baseurl?: string
  query?: object
  headers?: Record<string, string>
  method?: string
  data?: object
  formdata?: Record<string, any>
  body?: string | FormData
  credentials?: RequestCredentials
  json?: boolean
  file?: boolean
  retry?: RetryOptions
  verifyResponse?: VerifyResponseFn
  return_url?: boolean
  cache?: Record<string, Record<string, CachedResponse>>
  useCache?: boolean | ((url: string, options: FetchOptions) => boolean)
  resetCache?: boolean
  cacheLifetime?: number
  /** Called when the response is a 401. Resolves to a fresh token to retry the request once with (same
   * `auth.type` and `authHeader`), or to nothing to give up and return the 401 response as usual. */
  onUnauthorized?: () => Promise<string | null | undefined>
}

export interface FetchApi {
  (url: string, options?: FetchOptions): Promise<any>
  resetCache: () => void
  invalidateCache: (match: string | ((url: string, verb: string) => boolean)) => void
  configure: (config: {
    defaults?: FetchOptions
    verbs?: Verb[]
    helpers?: Record<
      string,
      FetchApiHelperFn
    >
  }) => FetchApi
  [key: string]: any
}

/** Fetch enchancing function for calling apis.
 * @param {String} url - url to get resource.
 * @param {Object} options - options object used in fetch.
 * @param {Object} options.auth - data to add to authentication header
 * @param {Object} options.auth.type - authentication type
 * @param {Object} options.auth.token - authentication token
 * @param {string} options.baseurl - base url to prepend to given url.
 * @param {Object} options.query - object to append to url as query parameters.
 * @param {Object} options.headers - headers to add to the request
 * @param {string} options.method - method to use in request.
 * @param {Object} options.data - object to stringify into an application/json payload for the request
 * @param {Object} options.formdata - object of entries encode into an application/x-www-form-urlencoded encoded data payload for the request.
 * @param {string} options.body - raw text data to send with request.
 * @param {boolean} options.json - wether this request returns parsed json as a response or just a fetch request response.
 * @param {Function} options.verifyResponse - function to call to verify the obtained response.
 * @param {boolean} options.return_url - Wether to return the request's url instead of calling the request.
 * @param {Object} options.cache - cache object to use for setting/getting data from cache.
 * @param {boolean} options.useCache - wether to use the given cache for setting/getting cached data.
 * @param {boolean} options.resetCache - wether to ignore any data already in the given cache and to reset it with the results.
 * @returns {Promise} resolved returned by fetch().
 */
async function __fetchApi(url: string, _options: FetchOptions): Promise<any> {
  const options = _options || {};
  // Copied, never mutated in place: callers (ApiEndpoint definitions) share one options object across
  // calls, so writing the Authorization header into it would leak one request's token into the next.
  options.headers = { ...(options.headers || {}) };

  if (options.query) {
    const prefix = url.indexOf('?') === -1 ? '?' : '&';
    url = url + prefix + queryString.stringify(options.query);
    delete options.query;
  }

  if (options.baseurl) {
    url = `${options.baseurl}${url}`;
    delete options.baseurl;
  }

  if (options.return_url) {
    return url;
  }

  if (options.formdata) {
    const fd = new FormData();
    Object.entries(options.formdata).forEach(([name, value]) => {
      if (value && value.file && value.filename) {
        fd.append(name, value.file, value.filename)
      } else {
        fd.append(name, value)
      }
    })

    options.body = fd
    delete options.formdata
  } else if (options.data) {
    options.body = JSON.stringify(options.data)
    options.headers['Content-Type'] = 'application/json'
    delete options.data
  }

  const authHeader = options.authHeader || "Authorization"
  const authType = options.auth?.type
  if (options.auth) {
    if (options.auth.type && options.auth.token) {
      options.headers[authHeader] = `${options.auth.type} ${options.auth.token}`;
    }
    delete options.auth
  }

  const {
    json,
    file,
    cache,
    useCache: useCacheOption,
    resetCache,
    cacheLifetime,
    verifyResponse,
    retry: retryOptions,
    abortController,
    onUnauthorized,
  } = options;
  delete options.onUnauthorized

  if (abortController && !options.signal) {
    options.signal = abortController.signal
  }

  const useCache =
    (typeof useCacheOption === 'function')
      ? useCacheOption(url, options)
      : useCacheOption
  delete options.json
  delete options.cache
  delete options.useCache
  delete options.cacheLifetime
  delete options.resetCache
  delete options.retry
  delete options.verifyResponse
  const method = (options.method || 'get').toLowerCase()
  const now = new Date().getTime()

  if (cache && useCache) {
    const cachedResponse = (cache[method] || {})[url]

    if (cachedResponse && !resetCache) {
      if (cachedResponse.expires < now) {
        delete cache[method][url]
      } else {
        return Promise.resolve(cachedResponse.response)
      }
    }
  }

  return retryAsyncPromise(async () => {
    let fetchResponse = await fetch(url, options as RequestInit)
    if (fetchResponse.status === 401 && onUnauthorized) {
      const freshToken = await onUnauthorized()
      if (freshToken) {
        fetchResponse = await fetch(url, {
          ...options,
          headers: { ...options.headers, [authHeader]: `${authType || 'Bearer'} ${freshToken}` },
        } as RequestInit)
      }
    }
    let response: Response | Blob | any = fetchResponse

    if (json) {
      response = await response.json()
    } else if (file) {
      response = await response.blob()
    }

    if (verifyResponse && typeof verifyResponse === "function")
      await verifyResponse(fetchResponse, response, url, options)

    if (useCache && cache) {
      (cache[method] || (cache[method] = {}))[url] = {
        expires: now + cacheLifetime!,
        response,
      }
    }

    return response
  }, retryOptions)
}


__fetchApi.cache = {};

const fetchApi: FetchApi = (url, options) =>
  __fetchApi(url, {
    cache:__fetchApi.cache,
    ...fetchApi.DEFAULT_OPTIONS,
    ...options,
  })

// The live cache is `__fetchApi.cache`, the object actually handed to every call above. These two
// helpers MUST mutate that object rather than rebind a property, which is what the old
// `fetchApi.cache = {}` did: it replaced a copy nothing ever read, so resetting the cache silently
// did nothing and a stale lookup response survived until a hard refresh.
fetchApi.cache = __fetchApi.cache
fetchApi.resetCache = () => {
  Object.keys(__fetchApi.cache).forEach(verb => { delete __fetchApi.cache[verb] })
}

/** Drops cached responses for one resource, so the next read of it goes to the server.
 *
 * Call it after a write whose result other views read back through a `useCache` lookup. A cached
 * entry has no expiry of its own (no `cacheLifetime` is configured, so `expires` is NaN and the
 * "is it stale?" comparison is always false), so nothing else will ever evict it.
 *
 * @param match a substring of the cached URL, or a predicate over (url, verb).
 */
fetchApi.invalidateCache = (match: string | ((url: string, verb: string) => boolean)) => {
  const matches = typeof match === 'function' ? match : (url: string) => url.includes(match)

  const cache: Record<string, Record<string, CachedResponse>> = __fetchApi.cache

  Object.entries(cache).forEach(([verb, byUrl]) => {
    Object.keys(byUrl || {}).forEach(url => {
      if (matches(url, verb)) delete byUrl[url]
    })
  })
}

fetchApi.configure = function fetchApi_configure({
  defaults,
  verbs = ['get', 'post', 'put', 'delete'],
  helpers
}) {

  fetchApi.DEFAULT_OPTIONS = defaults || {};
  let { baseurl } = fetchApi.DEFAULT_OPTIONS;

  if (baseurl && baseurl[baseurl.length - 1] !== '/') {
    baseurl += '/';
  }

  fetchApi.DEFAULT_OPTIONS.baseurl = baseurl;

  [[], ...Object.entries(helpers || {})].forEach(([helperName, helperFn]) => {
    const helper: any = (url: string, options: FetchOptions) => {
      return helperFn ? helperFn(url, options, fetchApi) : fetchApi(url, options);
    }

    if (helperName) {
      fetchApi[helperName] = helper;
    }

    helper.url = (url: string) => helper(url, { return_url: true });

    verbs.forEach((verb) => {
      helper[verb] = (url: string, options: FetchOptions) =>
        helper(url, { method: verb, ...options });
    });
  });

  fetchApi.configured = true;

  return fetchApi;
};



export default fetchApi;
